import { Plural, Trans, useLingui } from "@lingui/react/macro";
import React, { useCallback, useRef, useState } from "react";
import { useFYC } from "components/FindYourCruise/useFYC";
import { navigate } from "gatsby";
import clsx from "clsx";
import { FirstLastDepartureCruiseDates } from "types/graphql-types";
import { uiStateToSerializableParams } from "components/Algolia/utils";
import { ConfiguredInstantSearch } from "components/Algolia/ConfiguredInstantSearch";
import type { UiState } from "instantsearch.js";
import { DepartureDateFilterDropDown } from "components/FindYourCruise/Components/Filters/DepartureDateFilter";
import { ButtonWithCruiseCounter } from "components/FindYourCruise/Components/ButtonWithCruiseCounter";
import { Autocomplete } from "components/FindYourCruise/Components/Autocomplete/Autocomplete";
import OpenModalAutocompleteButton from "components/FindYourCruise/Components/ModalAutocomplete/OpenModalAutocompleteButton";
import OpenModalDepartureDateButton from "components/FindYourCruise/Components/ModalDepartureDate/OpenModalDepartureDateButton";
import * as styles from "./FindYourCruiseSearchBox.module.scss";
import { useSticky } from "@silversea-ssc/ui-components-react";
import ConfiguredConfigure from "components/Algolia/ConfiguredConfigure";
import { buildAlgoliaRouting } from "components/FindYourCruise/routing";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { useSearchAnalytics } from "components/Algolia/algoliaAnalytics";
import { useLocation } from "@gatsbyjs/reach-router";
import { approachGuidesInsiderValue } from "src/utils/approach-guides";
import { stringPresent } from "src/utils/strings";

interface FindYourCruiseSearchBoxProps {
  fycPageUrl: string | undefined;
  firstLastDepartureCruiseDates: FirstLastDepartureCruiseDates;
  backgroundColor: "white" | "neutral01";
  enableSticky: boolean;
  lowTranslateY?: boolean;
}

const FindYourCruiseSearchBox: React.FC<FindYourCruiseSearchBoxProps> = ({
  fycPageUrl,
  firstLastDepartureCruiseDates,
  backgroundColor,
  enableSticky,
  lowTranslateY = false
}) => {
  const { t } = useLingui();
  const language = useLanguage();
  const location = useLocation();
  const containerRef = useRef(null);
  const stickyMobileContainerRef = useRef(null);
  const isSticky = useSticky(containerRef, 0) && enableSticky;
  const isStickyMobileContainer =
    useSticky(stickyMobileContainerRef, 0) && enableSticky;

  const promoAlgoliaCodeMapping = {};

  const { filterDefaultSearch, localizedSearchIndexName } = useFYC(undefined);
  const routing = localizedSearchIndexName
    ? buildAlgoliaRouting(localizedSearchIndexName, language, undefined, {})
    : undefined;

  const [searchParams, setSearchParams] = useState({});

  const updateSearchStateChange = useCallback(
    (uiState: UiState) => {
      if (!localizedSearchIndexName) return;

      const filteredSearchState = uiStateToSerializableParams(
        uiState,
        localizedSearchIndexName,
        {}
      );
      setSearchParams(filteredSearchState);
    },
    [localizedSearchIndexName]
  );

  const onSubmit = useCallback(() => {
    if (!routing) return;

    const routeState = routing.stateMapping?.stateToRoute(searchParams);
    if (routeState) {
      const insiderParam = approachGuidesInsiderValue(location.search);
      const newParams = routing.router?.createURL(searchParams);
      if (newParams) {
        const newQueryString = new URL(newParams);
        if (stringPresent(insiderParam))
          newQueryString.searchParams.append("insider", insiderParam);
        const newUrl = `${fycPageUrl}${newQueryString.search}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navigate(newUrl as any); // wrong type in gatsby
      }
    }
    return false;
  }, [routing, searchParams, location.search, fycPageUrl]);

  const { onStateChange } = useSearchAnalytics(
    "homepage",
    promoAlgoliaCodeMapping,
    updateSearchStateChange
  );

  if (!localizedSearchIndexName) return null;

  return (
    <ConfiguredInstantSearch
      indexName={localizedSearchIndexName}
      onStateChange={onStateChange}
    >
      <ConfiguredConfigure
        filters={filterDefaultSearch}
        hitsPerPage={0}
        distinct
      />
      <div
        ref={containerRef}
        className={clsx(styles.sectionContainer, {
          [styles.sectionContainerGray]: backgroundColor == "neutral01",
          [styles.sectionContainerStickyEnabled]: enableSticky,
          [styles.sectionContainerSticky]: isSticky
        })}
        data-content-name={
          isSticky ? "find_your_cruise_sticky" : "find_your_cruise"
        }
      >
        <div
          className={clsx(styles.container, styles.withNewMobileFilters, {
            [styles.lowTranslateY]: lowTranslateY
          })}
        >
          <div className={styles.title}>
            <Trans>Find your cruise</Trans>
          </div>

          <div className={styles.mobileFilters}>
            <OpenModalAutocompleteButton
              indexName={localizedSearchIndexName}
              label={t`Where to go?`}
              emptyPlaceholder={t`Take me anywhere ↓`}
            />
            <OpenModalDepartureDateButton
              firstLastDepartureCruiseDates={firstLastDepartureCruiseDates}
              label={t`Which period?`}
              emptyPlaceholder={t`Cruise in any period ↓`}
            />
          </div>

          <div className={styles.filters}>
            <Autocomplete
              placeholder={t`Where to go?`}
              openOnFocus={true}
              indexName={localizedSearchIndexName}
              dataContentName="where_to_go"
              panelClassName={styles.autocompletePanel}
              backgroundColor={isSticky ? "titanium-dark" : "white"}
              emptyPlaceholder={t`Take me anywhere ↓`}
            />

            <DepartureDateFilterDropDown
              firstLastDepartureCruiseDates={firstLastDepartureCruiseDates}
              backgroundColor={isSticky ? "titanium-dark" : "white"}
              contentWrapperClassName={styles.dateDropdownContentWrapper}
              emptyLabelPlaceholder={t`Cruise in any period`}
            />
          </div>
          <div className={styles.openModal}>
            <ButtonWithCruiseCounter
              onClick={onSubmit}
              full
              dataContentPiece="submit_button"
              noWrap
              label={buttonLabel}
              variant="accent"
            />
          </div>

          <ButtonWithCruiseCounter
            className={styles.buttonContainer}
            variant="accent"
            renderAs="button"
            onClick={onSubmit}
            dataContentPiece="submit_button"
            label={buttonLabel}
            noWrap
          />
        </div>
      </div>
      <div
        data-content-name="find_your_cruise_mobile_sticky"
        ref={stickyMobileContainerRef}
        className={clsx(styles.stickyMobile, {
          [styles.stickyMobileEnabledEnabled]: enableSticky,
          [styles.stickyMobileEnabled]: isStickyMobileContainer
        })}
        onClick={onSubmit}
      >
        <div className={styles.stickyMobileContent}>
          <div className={styles.title}>
            <Trans>Find your cruise</Trans>
          </div>
          <ButtonWithCruiseCounter
            size="small"
            dataContentPiece="submit_button"
            noWrap
            label={buttonLabel}
            variant="accent"
          />
        </div>
      </div>
    </ConfiguredInstantSearch>
  );
};

const buttonLabel = (_appliedFiltersCount, resultsFoundCount) => (
  <React.Fragment>
    <Plural
      value={resultsFoundCount}
      one="View # Cruise"
      other="View # Cruises"
    />
  </React.Fragment>
);

export default FindYourCruiseSearchBox;
